import {
    AfterContentInit,
    ChangeDetectorRef,
    Component,
    ContentChildren,
    ElementRef,
    EventEmitter,
    forwardRef,
    Input,
    OnDestroy,
    Output,
    QueryList,
    ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { Subscription } from 'rxjs';

/**
 * Opção de um <app-select-search>. Substitui <ion-select-option>.
 * Guarda o valor e serve de portador da label (lida a partir do textContent projetado).
 */
@Component({
    selector: 'app-select-search-option',
    template: `<ng-content></ng-content>`,
    styles: [':host { display: none; }'],
})
export class SelectSearchOptionComponent {
    @Input() value: any = null;
    @Input() disabled: boolean = false;

    constructor(public elementRef: ElementRef) { }

    /** Texto visível da opção (interpolação já renderizada). */
    public getLabel(): string {
        return (this.elementRef.nativeElement?.textContent || '').trim();
    }
}

interface IOption {
    value: any;
    label: string;
    disabled: boolean;
}

/**
 * Dropdown com pesquisa. Drop-in para <ion-select> (single-select).
 *
 * Suporta:
 *  - formControlName / [formControl] / [(ngModel)]  (ControlValueAccessor)
 *  - [value] + (selectionChange)                    (uso sem reactive forms)
 *  - (ionChange) com $event.detail.value            (compatibilidade com ion-select)
 *  - [disabled], placeholder
 *  - searchbar automática (escondida quando há poucas opções — ver searchThreshold)
 */
@Component({
    selector: 'app-select-search',
    templateUrl: './select-search.component.html',
    styleUrls: ['./select-search.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SelectSearchComponent),
            multi: true,
        },
    ],
})
export class SelectSearchComponent implements AfterContentInit, OnDestroy, ControlValueAccessor {

    @ContentChildren(SelectSearchOptionComponent, { descendants: true })
    private OptionComponents: QueryList<SelectSearchOptionComponent>;

    @ViewChild('popover') popover;
    @ViewChild('search') searchbar;
    @ViewChild('popContent', { read: ElementRef }) popContent: ElementRef;
    @ViewChild(CdkVirtualScrollViewport) viewport: CdkVirtualScrollViewport;

    @Input('placeholder') Placeholder: string = 'indique o valor';
    /** Nº de opções a partir do qual a searchbar aparece. */
    @Input() searchThreshold: number = 8;
    /** Comparação de valores (por defeito igualdade estrita, como o ion-select). */
    @Input() compareWith: (a: any, b: any) => boolean = (a, b) => a === b;
    /** Aceite por compatibilidade com o markup existente (não altera comportamento). */
    @Input('interface') interfaceMode: string;
    @Input() multiple: boolean = false;

    @Output() selectionChange: EventEmitter<any> = new EventEmitter<any>();
    /** Compatibilidade: emite { detail: { value } } como o ion-select. */
    @Output() ionChange: EventEmitter<any> = new EventEmitter<any>();

    public Options: IOption[] = [];
    public FilteredOptions: IOption[] = [];
    public SearchText: string = '';
    public IsOpen: boolean = false;
    public IsDisabled: boolean = false;
    public ActiveItem: IOption | null = null;

    private _value: any = null;
    private optionsSub: Subscription;

    private onChange: (value: any) => void = () => { };
    private onTouched: () => void = () => { };

    constructor(private cdr: ChangeDetectorRef) { }

    // --- ligação sem reactive forms ---
    @Input('value')
    set value(v: any) {
        this._value = v;
        this.cdr.markForCheck();
    }
    get value(): any {
        return this._value;
    }

    @Input('disabled')
    set disabled(v: boolean) {
        this.IsDisabled = v;
    }

    // --- ControlValueAccessor ---
    writeValue(value: any): void {
        this._value = value;
        this.cdr.markForCheck();
    }
    registerOnChange(fn: (value: any) => void): void {
        this.onChange = fn;
    }
    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled: boolean): void {
        this.IsDisabled = isDisabled;
    }

    // --- lifecycle ---
    ngAfterContentInit(): void {
        this.BuildOptions();
        this.optionsSub = this.OptionComponents.changes.subscribe(() => this.BuildOptions());
    }

    ngOnDestroy(): void {
        this.optionsSub?.unsubscribe();
    }

    /** Reconstrói a lista de opções a partir dos <app-select-search-option> projetados. */
    private BuildOptions(): void {
        // adiado para garantir que a interpolação das labels já foi renderizada.
        Promise.resolve().then(() => {
            this.Options = (this.OptionComponents?.toArray() || []).map(opt => ({
                value: opt.value,
                label: opt.getLabel(),
                disabled: !!opt.disabled,
            }));
            this.ApplyFilter();
            this.cdr.markForCheck();
        });
    }

    // --- template helpers ---
    get SelectedLabel(): string {
        if (this.multiple) {
            const values: any[] = Array.isArray(this._value) ? this._value : [];
            return this.Options
                .filter(o => values.some(v => this.compareWith(v, o.value)))
                .map(o => o.label)
                .join(', ');
        }
        const match = this.Options.find(o => this.compareWith(o.value, this._value));
        return match ? match.label : '';
    }

    get ShowSearch(): boolean {
        return this.Options.length > this.searchThreshold;
    }

    public IsSelected(item: IOption): boolean {
        if (this.multiple) return Array.isArray(this._value) && this._value.some(v => this.compareWith(v, item?.value));
        return this.compareWith(item?.value, this._value);
    }

    /** Altura da lista ajustada ao conteúdo (até 8 linhas visíveis, depois faz scroll). */
    get ViewportHeight(): number {
        const rowHeight = 40;
        const count = this.FilteredOptions?.length || 0;
        if (count === 0) return 72; // espaço para "sem resultados"
        return Math.min(count, 8) * rowHeight;
    }

    public OnSearch(ev: any): void {
        this.SearchText = ev?.detail?.value ?? ev?.target?.value ?? '';
        this.ApplyFilter();
        this.ActiveItem = this.FilteredOptions[0] || null;
    }

    public IsActive(item: IOption): boolean {
        return item === this.ActiveItem;
    }

    public OnKeyDown(ev: KeyboardEvent): void {
        const len = this.FilteredOptions.length;
        if (ev.key === 'ArrowDown' || ev.key === 'ArrowUp') {
            ev.preventDefault();
            if (!len) return;
            let idx = this.ActiveItem ? this.FilteredOptions.indexOf(this.ActiveItem) : -1;
            idx = ev.key === 'ArrowDown'
                ? (idx < len - 1 ? idx + 1 : 0)
                : (idx > 0 ? idx - 1 : len - 1);
            this.ActiveItem = this.FilteredOptions[idx];
            this.viewport?.scrollToIndex(idx, 'auto');
        } else if (ev.key === 'Enter') {
            ev.preventDefault();
            if (this.ActiveItem && !this.ActiveItem.disabled) this.OnItemClick(this.ActiveItem);
        } else if (ev.key === 'Escape') {
            this.IsOpen = false;
        }
    }

    public OnItemClick(item: IOption): void {
        if (this.IsDisabled || item?.disabled) return;

        let value: any;

        if (this.multiple) {
            const current: any[] = Array.isArray(this._value) ? [...this._value] : [];
            const idx = current.findIndex(v => this.compareWith(v, item.value));

            if (idx > -1) current.splice(idx, 1);
            else current.push(item.value);

            value = current;
        } else {
            value = item.value;
            this.IsOpen = false;
        }

        this._value = value;

        this.onChange(value);
        this.onTouched();
        this.selectionChange.emit(value);
        this.ionChange.emit({
            detail: { value },
            preventDefault: () => { },
            stopPropagation: () => { },
        });
    }

    private ApplyFilter(): void {
        const text = (this.SearchText || '').toLowerCase().trim();
        this.FilteredOptions = text
            ? this.Options.filter(o => o.label.toLowerCase().includes(text))
            : this.Options.slice();
    }

    public FocusSearch(): void {
        this.ActiveItem = this.FilteredOptions.find(o => this.IsSelected(o)) || this.FilteredOptions[0] || null;
        if (this.ShowSearch) {
            setTimeout(() => this.searchbar?.setFocus(), 50);
        } else {
            setTimeout(() => this.popContent?.nativeElement?.focus(), 50);
        }
    }

    public OpenPopover(e: Event): void {
        if (this.IsDisabled) return;
        this.SearchText = '';
        this.ApplyFilter();
        this.popover.event = e;
        this.IsOpen = true;
    }

    public trackByFn(index: number, item: IOption): any {
        return index;
    }
}
